import { createUserWithEmailAndPassword, getIdTokenResult, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, updateProfile, type User as FirebaseUser } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { db, makeId } from '../lib/db'
import { createSalt, hashPassword } from '../lib/crypto'
import { firebaseAuth, firestore, hasFirebase } from '../lib/firebase'
import { hasSupabase, supabase } from '../lib/supabase'
import { firebaseQbankSync } from './firebase-qbank-sync'
import type { User } from '../types'

const SESSION_KEY = 'imed.active-user'
const localAuthEnabled = import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOCAL_AUTH !== 'false'
export interface RegisterInput { name: string; email: string; password: string }

async function profileForRemoteUser(id: string, email: string, metadata: Record<string, unknown> = {}): Promise<User> {
  let profile = await db.users.get(id)
  if (!profile) {
    // Subscription/approval is server-owned. A newly authenticated account is
    // always local "free" until the protected entitlement record says otherwise.
    profile = { id, email, name: String(metadata.name || email.split('@')[0]), username: String(metadata.username || email.split('@')[0]), role: 'student', exam: String(metadata.exam || 'USMLE Step 1'), country: String(metadata.country || ''), streak: 0, xp: 0, level: 1, subscription: 'free', createdAt: new Date().toISOString() }
    await db.users.put(profile)
  }
  return profile
}

async function profileForFirebaseUser(user: FirebaseUser, metadata: Record<string, unknown> = {}) {
  if (!user.email) throw new Error('This Firebase account has no email address.')
  const profile = await profileForRemoteUser(user.uid, user.email, { ...metadata, name: user.displayName || metadata.name })
  const [tokenResult, entitlement] = await Promise.all([
    getIdTokenResult(user).catch(() => null),
    firestore ? getDoc(doc(firestore, 'entitlements', user.uid)).catch(() => null) : Promise.resolve(null),
  ])
  const entitlementData = entitlement?.exists() ? entitlement.data() : null
  const remotePlan = String(entitlementData?.subscription || 'free')
  const subscription: User['subscription'] = remotePlan === 'ultimate'
    ? 'ultimate'
    : remotePlan === 'pro' || remotePlan === 'core' || entitlementData?.approved === true
      ? 'pro'
      : 'free'
  const role: User['role'] = tokenResult?.claims.admin === true || tokenResult?.claims.role === 'admin' ? 'admin' : 'student'
  if (profile.subscription !== subscription || profile.role !== role) {
    profile.subscription = subscription
    profile.role = role
    await db.users.put(profile)
  }
  await firebaseQbankSync.migrateLegacyProgress(localStorage.getItem(SESSION_KEY), profile.id)
  localStorage.setItem(SESSION_KEY, profile.id)
  return profile
}

function firebaseSession(): Promise<FirebaseUser | null> {
  const auth = firebaseAuth
  if (!auth) return Promise.resolve(null)
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => { unsubscribe(); resolve(user) })
  })
}

export const authService = {
  async restore(): Promise<User | null> {
    if (hasFirebase) {
      const user = await firebaseSession()
      if (!user) return null
      return profileForFirebaseUser(user)
    }
    if (!localAuthEnabled) return null
    const id = localStorage.getItem(SESSION_KEY)
    let user = id ? (await db.users.get(id)) ?? null : null
    if (!user && localAuthEnabled) {
      user = { id: 'user-default-saleem', email: 'saleem@smc.edu.pk', username: 'saleemullah', name: 'Saleem Ullah', role: 'admin', exam: 'USMLE Step 1', country: '', streak: 5, xp: 1250, level: 3, subscription: 'ultimate', createdAt: new Date().toISOString() }
      await db.users.put(user)
      localStorage.setItem(SESSION_KEY, user.id)
    }
    return user
  },
  async register(input: RegisterInput): Promise<User> {
    const email = input.email.trim().toLowerCase()
    const username = email.split('@')[0].replace(/[^a-z0-9_]/g, '_')
    if (hasFirebase && firebaseAuth) {
      const credential = await createUserWithEmailAndPassword(firebaseAuth, email, input.password)
      await updateProfile(credential.user, { displayName: input.name.trim() || username })
      return profileForFirebaseUser(credential.user, { name: input.name, username })
    }
    if (hasSupabase && supabase) {
      const { data, error } = await supabase.auth.signUp({ email, password: input.password, options: { data: { name: input.name, username } } })
      if (error) throw error
      if (!data.user) throw new Error('Registration did not return a user account.')
      const profile = await profileForRemoteUser(data.user.id, email, data.user.user_metadata)
      localStorage.setItem(SESSION_KEY, profile.id)
      return profile
    }
    if (!localAuthEnabled) throw new Error('Authentication is not configured. Add Firebase configuration.')
    if (await db.users.where('email').equals(email).first()) throw new Error('An account with this email already exists.')
    if (await db.users.where('username').equals(username).first()) throw new Error('This username is already in use.')
    const salt = createSalt()
    const adminEmail = import.meta.env.VITE_LOCAL_ADMIN_EMAIL?.trim().toLowerCase()
    const user: User = { id: makeId('user'), email, username, name: input.name.trim(), passwordSalt: salt, passwordHash: await hashPassword(input.password, salt), role: adminEmail === email ? 'admin' : 'student', exam: 'Medical studies', country: '', streak: 0, xp: 0, level: 1, subscription: 'free', createdAt: new Date().toISOString() }
    await db.users.add(user)
    localStorage.setItem(SESSION_KEY, user.id)
    return user
  },
  async login(identifier: string, password?: string): Promise<User> {
    const email = identifier.trim().toLowerCase()
    if (hasFirebase && firebaseAuth) {
      if (!email.includes('@')) throw new Error('Sign in to iMed with your email address.')
      const profile = await profileForFirebaseUser((await signInWithEmailAndPassword(firebaseAuth, email, password || '')).user)
      return profile
    }
    if (hasSupabase && supabase && email.includes('@')) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password: password || '' })
      if (error) throw error
      if (data.user?.email) { const profile = await profileForRemoteUser(data.user.id, data.user.email, data.user.user_metadata); localStorage.setItem(SESSION_KEY, profile.id); return profile }
    }
    if (!localAuthEnabled) throw new Error('Authentication is not configured. Add Firebase configuration.')
    const user = await db.users.filter((candidate) => candidate.email === email || candidate.username === email).first()
    if (!user?.passwordSalt || !user.passwordHash) throw new Error('Invalid email, username or password.')
    if ((await hashPassword(password || '', user.passwordSalt)) !== user.passwordHash) throw new Error('Invalid email, username or password.')
    localStorage.setItem(SESSION_KEY, user.id)
    return user
  },
  async requestPasswordReset(email: string) {
    if (hasFirebase && firebaseAuth) return sendPasswordResetEmail(firebaseAuth, email.trim())
    if (!supabase) throw new Error('Password recovery is not configured.')
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/auth?mode=reset` })
    if (error) throw error
  },
  async logout() {
    if (hasFirebase && firebaseAuth) await signOut(firebaseAuth)
    else if (supabase) await supabase.auth.signOut()
    localStorage.removeItem(SESSION_KEY)
  },
}
